// @flow

import React from 'react'
import styled, { keyframes } from 'styled-components'
import theme from 'styled-theming'
import hexToRgba from 'hex-to-rgba'

import { myTheme } from '../../styles'

// Styles
// ----------------------------------------

const { color, borderRadii, breakpoint } = myTheme

const buttonBackground = {
  mobile: {
    default: theme('mode', {
      light: hexToRgba(color.white, 0.8),
      dark: hexToRgba(color.darkBlueGray, 0.8),
    }),
    supportsBackdropFilter: theme('mode', {
      light: hexToRgba(color.white, 0.2),
      dark: hexToRgba(color.darkBlueGray, 0.2),
    }),
  },
  greaterThanMobile: theme('mode', {
    light: hexToRgba(color.black, 0.05),
    dark: hexToRgba(color.white, 0.05),
  }),
}

const Checkbox = styled.input`
  display: none; /* hides checkbox visually */
`
const WrapperLabel = styled.label`
  align-items: center;
  background-color: transparent;
  border-radius: ${borderRadii.three};
  display: flex;
  justify-content: center;
  width: 48px;
  height: 48px;

  transition: background-color 200ms ease-in-out;

  /* Hides inner icon before entrering frame (from animation) */
  overflow: hidden;

  @media screen and (min-width: 0) and (max-width: ${breakpoint.medium}) {
    background-color: ${buttonBackground.mobile.default};

    @supports (backdrop-filter: blur(20px)) {
      background-color: ${buttonBackground.mobile.supportsBackdropFilter};
      backdrop-filter: blur(20px);
    }
  }

  @media screen and (min-width: ${breakpoint.medium}) {
    background-color: transparent;

    &:hover {
      background-color: ${buttonBackground.greaterThanMobile};
    }
  }
`
const moveIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(100%);
  }
  25% {
    opacity: 0;
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`
const IconWrapper = styled.div`
  /*
    If the user clicks a bunch of times the text will highlight.
    IMO, this isn't a great user experience so I've disabled this
    native interaction.
  */
  user-select: none;

  /* Moves Icons down a pixel so it's visually centered */
  position: relative;
  top: 0.0625rem;
`
const Animation = styled.div`
  animation-name: ${moveIn};
  animation-timing-function: ease-out;
  animation-duration: 300ms;
  animation-delay: 250ms;
  animation-fill-mode: both;
`

type Props = {
  onClick: () => void,
  id: string,
  className?: string,
  nightMode: boolean,
}

type IconProps = { fill?: string }

const DayIcon = ({ fill }: IconProps) => (
  <Animation>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="25"
      height="25"
    >
      <path
        fill={fill}
        /* eslint-disable max-len */
        d="M14.64 13.5l.93.37c-.2.49-.5.93-.89 1.28-.59.55-1.37.85-2.18.85-.82 0-1.57-.3-2.18-.87-.38-.34-.68-.78-.87-1.26l.93-.37c.14.34.35.65.62.9.85.79 2.19.77 3.01.01.28-.25.49-.56.63-.91zM11.01 12h1c0-1.1-.9-2-2-2s-2 .9-2 2h1c0-.55.45-1 1-1s1 .45 1 1zm2 0h1c0-.55.45-1 1-1s1 .45 1 1h1c0-1.1-.9-2-2-2s-2 .9-2 2zm12 .5c0 .34-.22.64-.55.74a5.114 5.114 0 0 0-3.56 4.89c0 .77.17 1.52.5 2.21.15.31.09.66-.15.9a.79.79 0 0 1-.9.15c-.69-.33-1.44-.5-2.21-.5-2.24 0-4.21 1.43-4.89 3.56-.1.33-.4.55-.74.55-.34 0-.64-.22-.75-.55a5.114 5.114 0 0 0-4.89-3.56c-.77 0-1.52.17-2.21.5-.31.15-.66.09-.9-.15a.775.775 0 0 1-.15-.9c.33-.7.5-1.44.5-2.21 0-2.24-1.43-4.21-3.56-4.89-.33-.1-.55-.4-.55-.74 0-.34.22-.64.55-.75a5.114 5.114 0 0 0 3.56-4.89c0-.77-.17-1.52-.5-2.21a.775.775 0 0 1 .15-.9.79.79 0 0 1 .9-.15c.7.34 1.44.51 2.21.51 2.24 0 4.21-1.43 4.89-3.56.1-.33.4-.55.74-.55.34 0 .64.22.75.55a5.114 5.114 0 0 0 4.89 3.56c.77 0 1.52-.17 2.21-.51.31-.15.66-.09.9.15s.3.59.15.9c-.33.7-.5 1.44-.5 2.21 0 2.24 1.43 4.21 3.56 4.89.33.11.55.41.55.75zm-1.41 0a6.107 6.107 0 0 1-3.7-5.63c0-.74.13-1.46.39-2.14-.68.26-1.4.39-2.14.39-2.48 0-4.67-1.47-5.63-3.7a6.107 6.107 0 0 1-5.63 3.7c-.74 0-1.46-.13-2.14-.39.26.68.39 1.4.39 2.14 0 2.48-1.47 4.67-3.7 5.63 2.24.96 3.7 3.16 3.7 5.63 0 .74-.13 1.46-.39 2.14.68-.26 1.4-.39 2.14-.39 2.48 0 4.67 1.47 5.63 3.7.96-2.24 3.16-3.7 5.63-3.7.74 0 1.46.13 2.14.39-.26-.68-.39-1.4-.39-2.14 0-2.47 1.46-4.67 3.7-5.63zm-4.59 0c0 3.58-2.92 6.5-6.5 6.5s-6.5-2.92-6.5-6.5S8.93 6 12.51 6s6.5 2.92 6.5 6.5zm-1 0c0-3.03-2.47-5.5-5.5-5.5a5.51 5.51 0 0 0-5.5 5.5c0 3.03 2.47 5.5 5.5 5.5s5.5-2.47 5.5-5.5z"
        /* eslint-enable max-len */
      />
    </svg>
  </Animation>
)
DayIcon.defaultProps = {
  fill: color.darkSilver,
}
const NightIcon = ({ fill }: IconProps) => (
  <Animation>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="23"
      height="23"
    >
      <path
        fill={fill}
        /* eslint-disable max-len */
        d="M12.062 22.99c-6.651 0-12.06-5.4-12.06-12.033 0-4.977 2.566-9.062 6.862-10.92a.468.468 0 0 1 .516.101c.138.138.165.34.082.516a11.061 11.061 0 0 0-1.076 4.793c0 6.154 5.014 11.168 11.168 11.168 1.675 0 3.294-.368 4.794-1.076a.463.463 0 0 1 .515.082c.138.13.174.34.1.516-1.867 4.287-5.942 6.853-10.9 6.853zM6.147 1.417C2.853 3.312.92 6.8.92 10.957c0 6.127 4.996 11.113 11.141 11.113 4.15 0 7.618-1.932 9.513-5.225-1.288.46-2.64.69-4.03.69-6.67 0-12.088-5.419-12.088-12.088 0-1.39.23-2.742.69-4.03zm8.206 9.614a.454.454 0 0 1-.442-.589l1.104-3.615-2.88-2.3a.46.46 0 0 1 .286-.82h3.533L17.048.314c.12-.377.755-.377.874 0l1.095 3.395h3.533c.193 0 .368.12.432.303a.463.463 0 0 1-.147.516l-2.88 2.309 1.105 3.523a.459.459 0 0 1-.166.506.442.442 0 0 1-.534 0l-2.852-2.051-2.86 2.125a.526.526 0 0 1-.295.092zm1.481-4.729c.147.12.212.313.157.497l-.782 2.576 2.005-1.49a.455.455 0 0 1 .543 0l2.005 1.444-.782-2.502a.45.45 0 0 1 .157-.497l2.107-1.684h-2.558a.464.464 0 0 1-.442-.322L17.49 1.97l-.754 2.355a.464.464 0 0 1-.442.322h-2.557l2.097 1.656z"
        /* eslint-enable max-len */
      />
    </svg>
  </Animation>
)
NightIcon.defaultProps = {
  fill: color.lightBlueGray,
}

const ThemeToggle = ({
  onClick, id, className, nightMode,
}: Props) => (
  <WrapperLabel
    htmlFor={id}
    className={className}
  >
    <Checkbox
      id={id}
      onClick={onClick}
      type="checkbox"
    />
    <IconWrapper>{nightMode ? <DayIcon /> : <NightIcon />}</IconWrapper>
  </WrapperLabel>
)
ThemeToggle.defaultProps = {
  className: '',
}

export default ThemeToggle



// WEBPACK FOOTER //
// ./src/components/theme-toggle/index.js